import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
/**
 * Values a query parameter may take before it is serialised. `null`,
 * `undefined` and `''` are dropped rather than sent as empty parameters.
 */
export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue | readonly QueryValue[]>;
/** Base URL for the main REST API (`environment.API_ENDPOINT`). */
export const API_BASE_URL = new InjectionToken<string>('API_BASE_URL', {
  providedIn: 'root',
  factory: () => environment.API_ENDPOINT
});
/** Base URL for the AI service (`environment.API_ENDPOINT_AI`). */
export const AI_BASE_URL = new InjectionToken<string>('AI_BASE_URL', {
  providedIn: 'root',
  factory: () => environment.API_ENDPOINT_AI
});
/** Base URL for the payment service (`environment.PAYMENT_API_ENDPOINT`). */
export const PAYMENT_BASE_URL = new InjectionToken<string>('PAYMENT_BASE_URL', {
  providedIn: 'root',
  factory: () => environment.PAYMENT_API_ENDPOINT
});
/**
 * Builds `HttpParams`, skipping entries whose value is `null`, `undefined` or
 * the empty string.
 *
 * This is deliberate: several existing services hand-concatenate query strings
 * with `|| ''` fallbacks, which sends `?brand=&factory=` for absent filters.
 * Callers that genuinely need an empty-string parameter must set it on the
 * returned `HttpParams` themselves.
 */
export function buildParams(params?: QueryParams): HttpParams {
  let httpParams = new HttpParams();
  if (!params) {
    return httpParams;
  }
  for (const [key, raw] of Object.entries(params)) {
    const values = Array.isArray(raw) ? raw : [raw];
    for (const value of values) {
      if (value === null || value === undefined || value === '') {
        continue;
      }
      httpParams = httpParams.append(key, String(value));
    }
  }
  return httpParams;
}
/**
 * Thin wrapper over `HttpClient` that owns the base URL and query-param
 * construction, so individual services stop rebuilding
 * `environment.API_ENDPOINT + 'thing/'` by hand (247 occurrences at the time
 * of writing).
 *
 * Paths passed in are relative to the base URL and should not start with `/`.
 *
 * Auth headers and refresh handling stay in `AuthInterceptor` - this service
 * deliberately does not duplicate them.
 */
@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);
  get<T = any>(path: string, params?: QueryParams): Observable<T> {
    return this.http.get<T>(this.url(path), { params: buildParams(params) });
  }
  post<T = any>(path: string, body?: unknown, params?: QueryParams): Observable<T> {
    return this.http.post<T>(this.url(path), body, { params: buildParams(params) });
  }
  put<T = any>(path: string, body?: unknown, params?: QueryParams): Observable<T> {
    return this.http.put<T>(this.url(path), body, { params: buildParams(params) });
  }
  patch<T = any>(path: string, body?: unknown, params?: QueryParams): Observable<T> {
    return this.http.patch<T>(this.url(path), body, { params: buildParams(params) });
  }
  delete<T = any>(path: string, params?: QueryParams): Observable<T> {
    return this.http.delete<T>(this.url(path), { params: buildParams(params) });
  }
  /** Absolute URL for `path`. Useful for downloads and `window.open` targets. */
  url(path: string): string {
    return `${this.baseUrl}${path}`;
  }
}
